import { useReveal, reveal } from '../hooks/useReveal';

export default function Thesis() {
  const [sectionRef, isRevealed] = useReveal({ threshold: 0.1 });
  const [quoteRef, quoteRevealed] = useReveal({ threshold: 0.2 });

  return (
    <section className="py-32 md:py-40 relative overflow-visible">
      {/* Section divider - the nav lands here */}
      <div id="thesis" className="w-full h-px bg-border mb-24"></div>

      {/* Opening statement - MASSIVE */}
      <div ref={sectionRef} className="mb-24">
        <div className={`flex items-center gap-4 mb-8 ${reveal(isRevealed)}`}>
          <div className="w-16 h-1.5 bg-accent"></div>
          <p className="text-caption uppercase text-subtle tracking-widest">Point of View</p>
        </div>

        {/* The headline that stops the scroll */}
        <h2 className={`text-display-lg mb-6 ${reveal(isRevealed)}`} style={{ '--d': '60ms' }}>
          Knowledge is <span className="text-accent-ink">commoditized.</span>
        </h2>

        <p className={`text-statement text-muted max-w-2xl ${reveal(isRevealed)}`} style={{ '--d': '120ms' }}>
          The new currency is judgment, agency, and decision power.
        </p>
      </div>

      {/* The argument */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-24 overflow-visible">
        <div className="lg:col-span-7">
          <div className="space-y-6 text-body text-muted">
            <p className={`text-body-lg text-foreground ${reveal(isRevealed)}`} style={{ '--d': '160ms' }}>
              Most people use AI for the mundane—fixing typos, catching bugs,
              reviewing PRs.
            </p>

            <p className={reveal(isRevealed)} style={{ '--d': '200ms' }}>
              The ones who are really leveraging it work differently. They spend
              more time on architecture and judgment, then let AI execute at scale.
            </p>

            {/* Key insight - styled differently for rhythm */}
            <p className={`text-body-lg text-foreground pl-6 border-l-2 border-accent ${reveal(isRevealed)}`} style={{ '--d': '200ms' }}>
              The human provides conviction. The machine provides velocity.
            </p>

            <p className={reveal(isRevealed)} style={{ '--d': '200ms' }}>
              This is happening everywhere, not just in code. The question is no longer
              <em> can AI execute the work?</em> It's <em>can you trust it?</em>
            </p>
          </div>
        </div>

        {/* Visual element - MASSIVE typographic question mark - SIGNATURE */}
        <div
          className={`lg:col-span-5 hidden lg:flex items-start justify-end ${reveal(isRevealed)}`}
          style={{ '--d': '150ms' }}
        >
          <span
            style={{
              fontSize: 'clamp(16rem, 25vw, 28rem)',
              lineHeight: '0.75',
              color: 'rgba(196, 164, 132, 0.3)',
              marginRight: '-3rem',
              marginTop: '-1rem'
            }}
            className="font-display select-none"
            aria-hidden="true"
          >
            ?
          </span>
        </div>
      </div>

      {/* The pull quote - THE MOMENT */}
      <div ref={quoteRef} className="relative py-24 md:py-32 my-20">
        {/* Background accent - full bleed */}
        <div className="absolute inset-0 bg-surface -mx-6 sm:-mx-10 lg:-mx-16"></div>

        <blockquote className="relative max-w-4xl mx-auto text-center">
          {/* Opening quotation mark - typographic flourish */}
          <span
            className={`block text-accent font-display mb-6 ${reveal(quoteRevealed)}`}
            style={{ fontSize: 'clamp(4rem, 8vw, 6rem)', lineHeight: 0.5 }}
            aria-hidden="true"
          >
            "
          </span>

          <p className={`text-display-lg text-foreground ${reveal(quoteRevealed)}`} style={{ '--d': '80ms' }}>
            In code, you can verify.<br />
            <span className="text-accent-ink">In finance, you need conviction.</span>
          </p>

          {/* Attribution line */}
          <div className={`mt-8 flex items-center justify-center gap-4 ${reveal(quoteRevealed)}`} style={{ '--d': '160ms' }}>
            <div className="w-8 h-px bg-accent"></div>
            <span className="text-caption uppercase tracking-widest text-subtle">The Thesis</span>
            <div className="w-8 h-px bg-accent"></div>
          </div>
        </blockquote>
      </div>

      {/* The closer */}
      <div className="max-w-3xl">
        <p className={`text-body text-muted mb-6 ${reveal(quoteRevealed)}`}>
          Here's the thing: one error destroys trust in everything. If page one
          has a mistake, you have to validate the entire document. At that point,
          you might as well have built it yourself. The time savings disappear.
        </p>

        <p className={`text-body-lg text-foreground ${reveal(quoteRevealed)}`} style={{ '--d': '80ms' }}>
          The companies that win will be the ones building trust layers for AI
          execution—systems where you don't have to blindly believe, where every
          assumption is traceable, where errors surface instead of hide.
        </p>
      </div>
    </section>
  );
}
